import React, { useState, useEffect } from "react";
import { useLoading } from "../../context/LoadingContext";
import { Auth } from "aws-amplify";
import { jwtDecode } from "jwt-decode";
import { useNavigate } from "react-router-dom";
import SearchIcon from "@mui/icons-material/Search";
import WhatsAppIcon from "@mui/icons-material/WhatsApp";
import ChatIcon from "@mui/icons-material/Chat";
import EmailIcon from "@mui/icons-material/Email";
import { fetchData } from "../../services/BackendServices";
import ChatbotWidget from "../../components/ChatbotWidget";

const SellerFarmerDashboard = () => {
  const navigate = useNavigate();
  const { setLoading } = useLoading();
  const [userKybInfo, setUserKybInfo] = useState(null);
  const [isKybLoading, setIsKybLoading] = useState(true);
  const [activeUser, setActiveUser] = useState("");

  // Redirection Handlers
  const handleExploreFinancing = () =>
    navigate("/seller-farmer/finance/rfqoffers");
  const handleStartVerification = () => navigate("/seller-farmer/setting");

  // External Communication Links
  const handleWhatsAppClick = () =>
    window.open("https://wa.me/9836236245", "_blank");
  const handleEmailClick = () => navigate("/seller-farmer/support");
  const handleChatClick = () => navigate("/seller-farmer/messaging");

  useEffect(() => {
    const checkUser = async () => {
      try {
        const user = await Auth.currentAuthenticatedUser();
        const token = user.signInUserSession.idToken.jwtToken;
        const decodedToken = jwtDecode(token);
        const groups = decodedToken["cognito:groups"] || [];
        if (!groups.includes("AMTPL_SELLERS_AND_FARMERS")) {
          await Auth.signOut();
          navigate("/login/seller-farmer");
        } else {
          setActiveUser(user.attributes.email);
        }
      } catch (error) {
        console.error("Error checking user:", error);
        await Auth.signOut();
        navigate("/login/seller-farmer");
      }
    };

    checkUser();
  }, [navigate]);

  useEffect(() => {
    const checkUserKYB = async () => {
      if (!activeUser) return;
      setLoading(true);
      setIsKybLoading(true);
      try {
        const userKYBResponse = await fetchData({
          target: "listUserKYB",
          userOrder: "UserKybID",
          orderDirection: "ASC",
          rowStart: 0,
          pageSize: 1,
          filterTerms: { EmailID: activeUser },
        });

        if (userKYBResponse.items && userKYBResponse.items.length > 0) {
          setUserKybInfo(userKYBResponse.items[0]);
        } else {
          setUserKybInfo(null);
        }
      } catch (error) {
        console.error("Error fetching KYB info:", error);
        setUserKybInfo(null);
      } finally {
        setLoading(false);
        setIsKybLoading(false);
      }
    };

    checkUserKYB();
  }, [activeUser, setLoading]);

  return (
    <>
      <ChatbotWidget userType="seller-farmer" />
      <div className="container-GP p-4 bg-white shadow-sm">
        <div
          className="text-center p-3 mb-4 rounded-top"
          style={{
            backgroundColor: "rgb(222 107 20)",
            color: "white",
            fontWeight: "700",
          }}
        >
          <h2 style={{ fontWeight: "700" }}>Welcome to Grains Global!</h2>
        </div>

        <div className="input-group mb-4">
          <input
            type="text"
            className="form-control"
            placeholder="Search for grains, buyers, or deals..."
          />
          <button className="btn-search" type="button">
            <SearchIcon />
          </button>
        </div>

        <div className="row">
          {/* Do you want to add produce? */}
          <div className="col-md-4 mb-4">
            <div className="card h-150 card-dash-css">
              <div
                className="card-body"
                style={{ lineHeight: "30px", padding: "0 !important" }}
              >
                <h5
                  className="card-title"
                  style={{ fontWeight: "700", color: "#000" }}
                >
                  Do you want to add produce?
                </h5>
                <p className="card-text card-text-height">
                  Add produce for verified partners.
                </p>
                <div className="text-center">
                  <button
                    className="submit-btn-100"
                    onClick={() => navigate("/seller-farmer/produce")}
                  >
                    Add Produce
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Financed Offers */}
          <div className="col-md-4 mb-4">
            <div className="card h-150 card-dash-css">
              <div
                className="card-body"
                style={{ lineHeight: "30px", padding: "0 !important" }}
              >
                <h5
                  className="card-title"
                  style={{ fontWeight: "700", color: "#000" }}
                >
                  Financed Offers
                </h5>
                <p className="card-text card-text-height">
                  Secure funding for your sales to maximize trade capacity.
                </p>
                <div className="text-center">
                  <button
                    className="submit-btn-100"
                    onClick={handleExploreFinancing}
                  >
                    Explore Financing
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Market Requirement */}
          <div className="col-md-4 mb-4">
            <div className="card h-150 card-dash-css">
              <div
                className="card-body"
                style={{ lineHeight: "30px", padding: "0 !important" }}
              >
                <h5
                  className="card-title"
                  style={{ fontWeight: "700", color: "#000" }}
                >
                  Market Requirement
                </h5>
                <p className="card-text card-text-height">
                  Requirements trending for sellers
                </p>
                <div className="text-center">
                  <button
                    className="submit-btn-100-min"
                    onClick={() => navigate("/seller-farmer/requirement")}
                  >
                    Explore Requirement
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* KYB Status */}
          <div className="col-md-4 mb-4">
            <div className="card h-150 card-dash-css">
              <div
                className="card-body"
                style={{ lineHeight: "30px", padding: "0 !important" }}
              >
                {isKybLoading ? null : (
                  <h5
                    className="card-title"
                    style={{ fontWeight: "700", color: "#000" }}
                  >
                    Your KYB Status:{" "}
                    {(() => {
                      const s = userKybInfo?.Status;
                      if (!s || s === "") {
                        return <span className="status-color">Pending</span>;
                      }
                      if (s === "Pending") {
                        return <span className="status-color"> In Review</span>;
                      }
                      if (s === "Approved") {
                        return (
                          <span
                            style={{
                              color: "green",
                              fontWeight: "700",
                              fontSize: "15px",
                            }}
                          >
                            Approved
                          </span>
                        );
                      }
                      return <span className="status-color">Rejected</span>;
                    })()}
                  </h5>
                )}

                <p className="card-text card-text-height">
                  {isKybLoading ? (
                    <span>Loading...</span>
                  ) : userKybInfo?.Status === "Approved" ? (
                    <>Trade Confidently with Grains Global</>
                  ) : (
                    <>Ensure security and unlock full platform features.</>
                  )}
                </p>

                {isKybLoading
                  ? null
                  : (() => {
                      const s = userKybInfo?.Status;
                      if (!s || s === "") {
                        return (
                          <div className="text-center">
                            <button
                              className="submit-btn-100"
                              onClick={handleStartVerification}
                            >
                              Start verification
                            </button>
                          </div>
                        );
                      }
                      if (s === "Pending") {
                        return (
                          <div className="text-center">
                            <button className="submit-btn-100">
                              Not verification
                            </button>
                          </div>
                        );
                      }
                      if (s === "Rejected") {
                        return (
                          <div className="text-center">
                            <button className="submit-btn-100">Rejected</button>
                          </div>
                        );
                      }
                      return (
                        <div className="text-center">
                          <button disabled className="submit-btn-100">
                            Verified
                          </button>
                        </div>
                      );
                    })()}
              </div>
            </div>
          </div>

          {/* KYC Status */}
          <div className="col-md-4 mb-4">
            <div className="card h-150 card-dash-css">
              <div
                className="card-body"
                style={{ lineHeight: "30px", padding: "0 !important" }}
              >
                <h5
                  className="card-title"
                  style={{ fontWeight: "700", color: "#000" }}
                >
                  Your KYC Status: <span className="status-color">Pending</span>
                </h5>
                <p className="card-text card-text-height">
                  Ensure security and unlock full platform features.
                </p>
                <div className="text-center">
                  <button
                    className="submit-btn-100"
                    onClick={() => navigate("/seller-farmer/setting")}
                  >
                    Start verification
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Contact Support */}
          <div className="col-md-4 mb-4">
            <div className="card h-150 card-dash-css">
              <div
                className="card-body"
                style={{ lineHeight: "30px", padding: "0 !important" }}
              >
                <h5
                  className="card-title"
                  style={{ fontWeight: "700", color: "#000" }}
                >
                  Contact Support
                </h5>
                <div
                  className="dash-icon card-text-height display-flex-dash"
                  style={{ gap: "40px" }}
                >
                  <div
                    onClick={handleWhatsAppClick}
                    className="whats-css"
                    style={{ cursor: "pointer" }}
                  >
                    <WhatsAppIcon />
                  </div>
                  <div
                    onClick={handleEmailClick}
                    className="email-css"
                    style={{ cursor: "pointer" }}
                  >
                    <EmailIcon />
                  </div>
                  <div
                    onClick={handleChatClick}
                    className="chat-css"
                    style={{ cursor: "pointer" }}
                  >
                    <ChatIcon />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default SellerFarmerDashboard;
